import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button } from '../../components/UI';
import { reportSettingsAPI } from '../../services/api';
import {
  HiOutlineDocumentReport,
  HiOutlineClock,
  HiOutlineMail,
  HiOutlineCheck,
  HiOutlineExclamation,
  HiOutlineRefresh,
  HiOutlinePlus,
  HiOutlineX,
  HiOutlinePencilAlt,
  HiOutlineTrash,
  HiOutlineCog,
  HiOutlineUser,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Pagination from '../../components/UI/Pagination';

// ─── Field definitions ─────────────────────────────────────────────────────

const ADMIN_FIELDS = [
  { key: 'callsMade', label: 'Total Calls Made' },
  { key: 'callsConnected', label: 'Connected Calls' },
  { key: 'callsNotConnected', label: 'Not Connected Calls' },
  { key: 'callDuration', label: 'Total Call Duration' },
  { key: 'avgCallDuration', label: 'Avg Call Duration' },
  { key: 'leadsWorked', label: 'Leads Worked' },
  { key: 'leadsNew', label: 'New Leads' },
  { key: 'leadsConverted', label: 'Converted Leads' },
  { key: 'leadsHot', label: 'Hot Leads' },
  { key: 'followUpsDue', label: 'Follow-ups Due Today' },
  { key: 'followUpsCompleted', label: 'Follow-ups Completed' },
  { key: 'demoScheduled', label: 'Demos Scheduled' },
  { key: 'conversionRate', label: 'Conversion Rate' },
  { key: 'workingHours', label: 'Working Hours' },
  { key: 'hourlyCallBreakdown', label: 'Hourly Call Breakdown' },
  { key: 'telecallerWiseBreakdown', label: 'Telecaller-wise Breakdown' },
  { key: 'visits', label: 'Visits & Meetings' },
  { key: 'visitOutcomes', label: 'Visit Outcomes' },
  { key: 'productivity', label: 'Productivity (talk + visit time, utilisation)' },
];

const TELECALLER_FIELDS = [
  { key: 'callsMade', label: 'Total Calls Made' },
  { key: 'callsConnected', label: 'Connected Calls' },
  { key: 'callsNotConnected', label: 'Not Connected Calls' },
  { key: 'callDuration', label: 'Total Call Duration' },
  { key: 'avgCallDuration', label: 'Avg Call Duration' },
  { key: 'leadsWorked', label: 'Leads Worked' },
  { key: 'leadsConverted', label: 'Converted Leads' },
  { key: 'followUpsCompleted', label: 'Follow-ups Completed' },
  { key: 'demoScheduled', label: 'Demos Scheduled' },
  { key: 'conversionRate', label: 'Connection Rate' },
  { key: 'workingHours', label: 'Working Hours' },
  { key: 'hourlyCallBreakdown', label: 'Hourly Call Breakdown' },
  { key: 'visits', label: 'My Visits & Meetings' },
  { key: 'visitOutcomes', label: 'Visit Outcomes' },
  { key: 'productivity', label: 'Productivity (talk + visit time, utilisation)' },
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m++) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const label = h === 0 ? `12:${mm} AM` : h < 12 ? `${h}:${mm} AM` : h === 12 ? `12:${mm} PM` : `${h - 12}:${mm} PM`;
    TIME_OPTIONS.push({ value: `${hh}:${mm}`, label });
  }
}

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India (IST, UTC+5:30)' },
  { value: 'Asia/Dubai', label: 'UAE (GST, UTC+4)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT, UTC+8)' },
  { value: 'America/New_York', label: 'New York (EST, UTC-5)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST, UTC-8)' },
  { value: 'Europe/London', label: 'London (GMT, UTC+0)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET, UTC+1)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST, UTC+10)' },
];

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];


const MONTHLY_DAY_OPTIONS = [];
for (let d = 1; d <= 28; d++) {
  const suffix = d === 1 || d === 21 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
  MONTHLY_DAY_OPTIONS.push({ value: d, label: `${d}${suffix}` });
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECIPIENT_PAGE_SIZE = 4;

// ─── Time Picker Helpers ──────────────────────────────────────────────────

function parseSendTime(sendTime) {
  if (!sendTime) return { hour12: 9, minute: 0, period: 'AM' };
  const [h, m] = sendTime.split(':').map(Number);
  if (h === 0) return { hour12: 12, minute: m, period: 'AM' };
  if (h < 12) return { hour12: h, minute: m, period: 'AM' };
  if (h === 12) return { hour12: 12, minute: m, period: 'PM' };
  return { hour12: h - 12, minute: m, period: 'PM' };
}

function toSendTime(hour12, minute, period) {
  let h = hour12;
  if (period === 'AM' && hour12 === 12) h = 0;
  else if (period === 'PM' && hour12 !== 12) h = hour12 + 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const HOUR_ITEMS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTE_ITEMS = Array.from({ length: 60 }, (_, i) => i);
const PERIOD_ITEMS = ['AM', 'PM'];

// ─── Time Picker Component ────────────────────────────────────────────────

function TimePicker({ value, onChange, alwaysOn = false, hideLabel = false }) {
  const isCustom = alwaysOn || value !== '';
  const parsed = parseSendTime(value || '09:00');
  const [hour12, setHour12] = useState(parsed.hour12);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);
  const [open, setOpen] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const p = parseSendTime(value || '09:00');
    setHour12(p.hour12);
    setMinute(p.minute);
    setPeriod(p.period);
    // In alwaysOn mode, ensure parent always has a valid time
    if (alwaysOn && !value) {
      onChange(toSendTime(p.hour12, p.minute, p.period));
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggleCustom = (checked) => {
    if (checked) {
      onChange(toSendTime(hour12, minute, period));
    } else {
      onChange('');
    }
  };

  const handleHourChange = (h) => {
    setHour12(h);
    onChange(toSendTime(h, minute, period));
  };

  const handleMinuteChange = (m) => {
    setMinute(m);
    onChange(toSendTime(hour12, m, period));
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
    onChange(toSendTime(hour12, minute, p));
  };

  const toggleDropdown = (which) => {
    setOpen((prev) => (prev === which ? null : which));
  };

  return (
    <div>
      {!hideLabel && <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Send Time</label>}

      {!alwaysOn && (
        <label className="flex items-center gap-3 cursor-pointer group mb-3">
          <button
            type="button"
            role="switch"
            aria-checked={isCustom}
            onClick={() => handleToggleCustom(!isCustom)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40 ${
              isCustom ? 'bg-primary-600' : 'bg-secondary-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                isCustom ? 'translate-x-2' : '-translate-x-2'
              }`}
            />
          </button>
          <span className="text-sm font-medium text-secondary-700 group-hover:text-secondary-900 transition-colors">
            Custom time
          </span>
          {!isCustom && <span className="text-xs text-secondary-400 ml-1">— inherits org schedule</span>}
        </label>
      )}

      {isCustom && (
        <div ref={containerRef} className="relative flex items-center gap-1.5">
          {/* Hour */}
          <div className="relative flex-1">
            {!alwaysOn && <span className="block text-[9px] font-medium text-secondary-400 text-center mb-1">Hour</span>}
            <button
              type="button"
              onClick={() => toggleDropdown('hour')}
              className={`w-full text-center text-sm font-semibold transition-all duration-200 bg-white ${
                alwaysOn
                  ? 'px-3 py-2.5 rounded-xl border-2'
                  : 'px-2 py-2 rounded-lg border'
              } ${
                open === 'hour' ? 'border-primary-500 ring-2 ring-primary-500/10 text-primary-700' : alwaysOn ? 'border-secondary-200 text-secondary-900 hover:border-primary-400' : 'border-secondary-200 text-secondary-900 hover:border-primary-400'
              }`}
            >
              {String(hour12).padStart(2, '0')}
            </button>
            {open === 'hour' && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg z-50 max-h-36 overflow-y-auto py-0.5 animate-in fade-in zoom-in duration-150">
                {HOUR_ITEMS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => { handleHourChange(h); setOpen(null); }}
                    className={`w-full px-2 py-1 text-xs text-center transition-colors ${
                      hour12 === h ? 'bg-primary-100 text-primary-700 font-semibold' : 'text-secondary-700 hover:bg-primary-50'
                    }`}
                  >
                    {String(h).padStart(2, '0')}
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className={`font-bold text-secondary-300 select-none ${alwaysOn ? 'text-sm' : 'text-base mt-4'}`}>:</span>

          {/* Minute */}
          <div className="relative flex-1">
            {!alwaysOn && <span className="block text-[9px] font-medium text-secondary-400 text-center mb-1">Min</span>}
            <button
              type="button"
              onClick={() => toggleDropdown('minute')}
              className={`w-full text-center text-sm font-semibold transition-all duration-200 bg-white ${
                alwaysOn
                  ? 'px-3 py-2.5 rounded-xl border-2'
                  : 'px-2 py-2 rounded-lg border'
              } ${
                open === 'minute' ? 'border-primary-500 ring-2 ring-primary-500/10 text-primary-700' : alwaysOn ? 'border-secondary-200 text-secondary-900 hover:border-primary-400' : 'border-secondary-200 text-secondary-900 hover:border-primary-400'
              }`}
            >
              {String(minute).padStart(2, '0')}
            </button>
            {open === 'minute' && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg z-50 max-h-36 overflow-y-auto py-0.5 animate-in fade-in zoom-in duration-150">
                {MINUTE_ITEMS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { handleMinuteChange(m); setOpen(null); }}
                    className={`w-full px-2 py-1 text-xs text-center transition-colors ${
                      minute === m ? 'bg-primary-100 text-primary-700 font-semibold' : 'text-secondary-700 hover:bg-primary-50'
                    }`}
                  >
                    {String(m).padStart(2, '0')}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* AM / PM */}
          <div className="relative flex-1">
            {!alwaysOn && <span className="block text-[9px] font-medium text-secondary-400 text-center mb-1">AM/PM</span>}
            <button
              type="button"
              onClick={() => toggleDropdown('period')}
              className={`w-full text-center text-sm font-semibold transition-all duration-200 bg-white ${
                alwaysOn
                  ? 'px-3 py-2.5 rounded-xl border-2'
                  : 'px-2 py-2 rounded-lg border'
              } ${
                open === 'period' ? 'border-primary-500 ring-2 ring-primary-500/10 text-primary-700' : alwaysOn ? 'border-secondary-200 text-secondary-900 hover:border-primary-400' : 'border-secondary-200 text-secondary-900 hover:border-primary-400'
              }`}
            >
              {period}
            </button>
            {open === 'period' && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg z-50 py-0.5 animate-in fade-in zoom-in duration-150">
                {PERIOD_ITEMS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { handlePeriodChange(p); setOpen(null); }}
                    className={`w-full px-2 py-1.5 text-xs text-center transition-colors ${
                      period === p ? 'bg-primary-100 text-primary-700 font-semibold' : 'text-secondary-700 hover:bg-primary-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!alwaysOn && (
        <p className="text-xs text-secondary-400 mt-1.5">
          {isCustom ? 'Set a custom send time for this recipient.' : 'Leave off to inherit the organization schedule.'}
        </p>
      )}
    </div>
  );
}

// ─── Toggle Component ──────────────────────────────────────────────────────

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40 ${
          checked ? 'bg-primary-600' : 'bg-secondary-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-2' : '-translate-x-2'
          }`}
        />
      </button>
      <span className="text-sm font-medium text-secondary-700 group-hover:text-secondary-900 transition-colors">
        {label}
      </span>
    </label>
  );
}

// ─── Checkbox Group Component ──────────────────────────────────────────────

function CheckboxGroup({ fields, values, onChange, title }) {
  const allChecked = fields.every((f) => values[f.key] !== false);
  const toggleAll = () => {
    const newVal = !allChecked;
    const updated = {};
    fields.forEach((f) => { updated[f.key] = newVal; });
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-secondary-800">{title}</h4>
        <button type="button" onClick={toggleAll} className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors">
          {allChecked ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map((field) => (
          <label key={field.key} className="flex items-center gap-2 cursor-pointer group">
            <input type="checkbox" checked={values[field.key] !== false} onChange={(e) => onChange({ [field.key]: e.target.checked })} className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500" />
            <span className="text-sm text-secondary-600 group-hover:text-secondary-800 transition-colors">{field.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Recipient Modal Component ─────────────────────────────────────────────

function RecipientModal({ isOpen, onClose, onSave, recipient, isEdit, orgDefaults }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    frequencies: ['daily'],
    weeklyDay: 1,
    monthlyDay: 1,
    sendTime: '',
    timezone: '',
  });
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (recipient) {
        setForm({
          name: recipient.name || '',
          email: recipient.email || '',
          frequencies: recipient.frequencies || ['daily'],
          weeklyDay: recipient.weeklyDay ?? 1,
          monthlyDay: recipient.monthlyDay ?? 1,
          sendTime: recipient.sendTime || '',
          timezone: recipient.timezone || '',
        });
      } else {
        setForm({ name: '', email: '', frequencies: ['daily'], weeklyDay: 1, monthlyDay: 1, sendTime: '', timezone: '' });
      }
    }
  }, [isOpen, recipient]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleFrequency = (freq) => {
    setForm((prev) => {
      const current = prev.frequencies;
      if (current.includes(freq)) {
        if (current.length <= 1) return prev;
        return { ...prev, frequencies: current.filter((f) => f !== freq) };
      }
      return { ...prev, frequencies: [...current, freq] };
    });
  };

  const handleSave = () => {
    if (!form.email.trim()) { toast.error('Email is required.'); return; }
    if (!EMAIL_REGEX.test(form.email.trim())) { toast.error('Please enter a valid email address.'); return; }
    if (form.frequencies.length === 0) { toast.error('At least one frequency is required.'); return; }
    onSave({
      name: form.name.trim() || '',
      email: form.email.trim().toLowerCase(),
      frequencies: form.frequencies,
      weeklyDay: form.weeklyDay,
      monthlyDay: form.monthlyDay,
      sendTime: form.sendTime || null,
      timezone: form.timezone || null,
    });
  };

  const showWeekly = form.frequencies.includes('weekly');
  const showMonthly = form.frequencies.includes('monthly');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div ref={modalRef} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
          <h3 className="text-lg font-semibold text-secondary-900">{isEdit ? 'Edit Recipient' : 'Add Recipient'}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-400 hover:text-secondary-600 transition-colors">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Name & Email — side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="John Doe" className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Email Address <span className="text-danger-500">*</span></label>
              <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="recipient@example.com" disabled={isEdit} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200 disabled:bg-secondary-50 disabled:text-secondary-400" />
            </div>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">Report Frequency <span className="text-danger-500">*</span></label>
            <div className="flex flex-wrap gap-2">
              {FREQUENCY_OPTIONS.map((opt) => {
                const active = form.frequencies.includes(opt.value);
                return (
                  <button key={opt.value} type="button" onClick={() => toggleFrequency(opt.value)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${active ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/30' : 'bg-secondary-50 text-secondary-600 border border-secondary-200 hover:border-primary-400 hover:text-primary-600'}`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-secondary-400 mt-1.5">Select one or more frequencies for this recipient.</p>
          </div>

          {showWeekly && (
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Weekly Day</label>
              <select value={form.weeklyDay} onChange={(e) => setForm((prev) => ({ ...prev, weeklyDay: parseInt(e.target.value, 10) }))} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
                {WEEKDAY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          )}

          {showMonthly && (
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Monthly Day</label>
              <select value={form.monthlyDay} onChange={(e) => setForm((prev) => ({ ...prev, monthlyDay: parseInt(e.target.value, 10) }))} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
                {MONTHLY_DAY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          )}

          <div className="border-t border-secondary-100" />

          {/* Send Time */}
          <TimePicker
            value={form.sendTime}
            onChange={(val) => setForm((prev) => ({ ...prev, sendTime: val }))}
          />

          {/* Timezone */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Timezone</label>
            <select value={form.timezone} onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
              <option value="">Use org timezone</option>
              {TIMEZONE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50/50 rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave}>{isEdit ? 'Save Changes' : 'Add Recipient'}</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Role Schedule Modal Component ─────────────────────────────────────────

const ROLE_LABELS = { admin: 'Admin', telecaller: 'Telecaller' };

function RoleScheduleModal({ isOpen, onClose, onSave, role, initialData, orgDefaults }) {
  const [form, setForm] = useState({ frequencies: [], weeklyDay: 1, monthlyDay: 1, sendTime: '', timezone: '' });
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialData) {
      setForm({
        frequencies: initialData.frequencies || (initialData.frequency ? [initialData.frequency] : []),
        weeklyDay: initialData.weeklyDay ?? 1,
        monthlyDay: initialData.monthlyDay ?? 1,
        sendTime: initialData.sendTime || '',
        timezone: initialData.timezone || '',
      });
    } else if (isOpen) {
      setForm({ frequencies: [], weeklyDay: 1, monthlyDay: 1, sendTime: '', timezone: '' });
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleFrequency = (freq) => {
    setForm((prev) => {
      const current = prev.frequencies;
      if (current.includes(freq)) {
        if (current.length <= 1) return prev;
        return { ...prev, frequencies: current.filter((f) => f !== freq) };
      }
      return { ...prev, frequencies: [...current, freq] };
    });
  };

  const handleSave = () => {
    onSave(role, {
      frequencies: form.frequencies.length > 0 ? form.frequencies : null,
      weeklyDay: form.frequencies.includes('weekly') ? form.weeklyDay : null,
      monthlyDay: form.frequencies.includes('monthly') ? form.monthlyDay : null,
      sendTime: form.sendTime || null,
      timezone: form.timezone || null,
    });
  };

  const showWeekly = form.frequencies.includes('weekly');
  const showMonthly = form.frequencies.includes('monthly');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div ref={modalRef} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
          <h3 className="text-lg font-semibold text-secondary-900">{ROLE_LABELS[role] || role} Schedule</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-400 hover:text-secondary-600 transition-colors">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-secondary-500">Configure when {ROLE_LABELS[role] || role} reports are sent. Leave fields as "Use org schedule" to inherit the organization-level settings.</p>

          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">Report Frequency</label>
            <div className="flex flex-wrap gap-2">
              {FREQUENCY_OPTIONS.map((opt) => {
                const active = form.frequencies.includes(opt.value);
                return (
                  <button key={opt.value} type="button" onClick={() => toggleFrequency(opt.value)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${active ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/30' : 'bg-secondary-50 text-secondary-600 border border-secondary-200 hover:border-primary-400 hover:text-primary-600'}`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-secondary-400 mt-1.5">Select one or more frequencies. Leave empty to use org-level frequency.</p>
          </div>

          {showWeekly && (
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Weekly Day</label>
              <select value={form.weeklyDay} onChange={(e) => setForm((prev) => ({ ...prev, weeklyDay: parseInt(e.target.value, 10) }))} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
                {WEEKDAY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          )}

          {showMonthly && (
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Monthly Day</label>
              <select value={form.monthlyDay} onChange={(e) => setForm((prev) => ({ ...prev, monthlyDay: parseInt(e.target.value, 10) }))} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
                {MONTHLY_DAY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          )}

          <div className="border-t border-secondary-100" />

          <TimePicker
            value={form.sendTime}
            onChange={(val) => setForm((prev) => ({ ...prev, sendTime: val }))}
          />

          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Timezone</label>
            <select value={form.timezone} onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
              <option value="">Use org timezone</option>
              {TIMEZONE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50/50 rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave}>Save Schedule</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

const ReportSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  // Collapsible recipient list state
  const [showRecipients, setShowRecipients] = useState(false);

  // Recipient card pagination
  const [recipientPage, setRecipientPage] = useState(1);

  // Role schedule modal state
  const [roleScheduleModal, setRoleScheduleModal] = useState({ open: false, role: null });

  // Load settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await reportSettingsAPI.getSettings();
      if (res.data?.success) setSettings(res.data.data);
    } catch (err) {
      console.error('Failed to load report settings:', err);
      setError('Failed to load report settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const getDefaults = () => ({
    enabled: false, sendTime: '18:00', timezone: 'Asia/Kolkata', frequency: 'daily', weeklyDay: 1, monthlyDay: 1,
    adminFields: { callsMade: true, callsConnected: true, callsNotConnected: true, callDuration: true, avgCallDuration: true, leadsWorked: true, leadsNew: true, leadsConverted: true, leadsHot: true, followUpsDue: true, followUpsCompleted: true, demoScheduled: true, conversionRate: true, workingHours: true, hourlyCallBreakdown: true, telecallerWiseBreakdown: true, visits: true, visitOutcomes: true, productivity: true },
    telecallerFields: { callsMade: true, callsConnected: true, callsNotConnected: true, callDuration: true, avgCallDuration: true, leadsWorked: true, leadsConverted: true, followUpsCompleted: true, demoScheduled: true, conversionRate: true, workingHours: true, hourlyCallBreakdown: true, visits: true, visitOutcomes: true, productivity: true },
    recipientRoles: { admin: true, telecaller: true },
    recipientRoleSchedules: {
      admin: { frequency: null, frequencies: [], sendTime: null, timezone: null, weeklyDay: null, monthlyDay: null },
      telecaller: { frequency: null, frequencies: [], sendTime: null, timezone: null, weeklyDay: null, monthlyDay: null },
    },
    customRecipients: [], lastSentDate: null,
  });

  const current = settings || getDefaults();
  const updateField = (key, value) => setSettings((prev) => ({ ...(prev || getDefaults()), [key]: value }));
  const updateAdminFields = (updates) => setSettings((prev) => ({ ...(prev || getDefaults()), adminFields: { ...(prev?.adminFields || getDefaults().adminFields), ...updates } }));
  const updateTelecallerFields = (updates) => setSettings((prev) => ({ ...(prev || getDefaults()), telecallerFields: { ...(prev?.telecallerFields || getDefaults().telecallerFields), ...updates } }));
  const updateRecipientRoles = (key, value) => setSettings((prev) => ({ ...(prev || getDefaults()), recipientRoles: { ...(prev?.recipientRoles || getDefaults().recipientRoles), [key]: value } }));

  // ── Custom recipient modal handlers ──────────────────────────────────────

  const openAddModal = () => {
    if ((current.customRecipients || []).length >= 20) { toast.error('Maximum 20 custom email recipients allowed.'); return; }
    setEditIndex(null);
    setModalOpen(true);
  };

  const openEditModal = (index) => { setEditIndex(index); setModalOpen(true); };

  const handleModalSave = (data) => {
    const cleaned = {
      name: data.name || '',
      email: data.email,
      frequencies: data.frequencies,
      weeklyDay: data.weeklyDay,
      monthlyDay: data.monthlyDay,
      ...(data.sendTime ? { sendTime: data.sendTime } : {}),
      ...(data.timezone ? { timezone: data.timezone } : {}),
    };
    setSettings((prev) => {
      const recipients = [...(prev?.customRecipients || [])];
      if (editIndex !== null) {
        recipients[editIndex] = cleaned;
      } else {
        if (recipients.some((r) => (typeof r === 'string' ? r : r.email) === cleaned.email)) { toast.error('This email is already in the list.'); return prev; }
        recipients.push(cleaned);
      }
      return { ...(prev || getDefaults()), customRecipients: recipients };
    });
    setModalOpen(false);
    setEditIndex(null);
  };

  const removeCustomRecipient = (index) => {
    setSettings((prev) => ({ ...(prev || getDefaults()), customRecipients: (prev?.customRecipients || []).filter((_, i) => i !== index) }));
    // Adjust page if needed
    const newLength = (current.customRecipients || []).length - 1;
    const maxPage = Math.max(1, Math.ceil(newLength / RECIPIENT_PAGE_SIZE));
    if (recipientPage > maxPage) setRecipientPage(maxPage);
  };

  // ── Role schedule modal handlers ──────────────────────────────────────────

  const openRoleScheduleModal = (role) => { setRoleScheduleModal({ open: true, role }); };

  const handleRoleScheduleSave = (role, data) => {
    setSettings((prev) => ({
      ...(prev || getDefaults()),
      recipientRoleSchedules: { ...(prev?.recipientRoleSchedules || getDefaults().recipientRoleSchedules), [role]: data },
    }));
    setRoleScheduleModal({ open: false, role: null });
  };

  // Save settings
  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await reportSettingsAPI.updateSettings(current);
      if (res.data?.success) { setSettings(res.data.data); toast.success('Report settings saved successfully!'); }
      else toast.error(res.data?.message || 'Failed to save settings');
    } catch (err) { console.error('Save settings error:', err); toast.error(err.response?.data?.message || 'Failed to save settings'); }
    finally { setSaving(false); }
  };

  // Send test report
  const handleSendNow = async () => {
    if (!window.confirm('This will generate and send a report email to all configured recipients right now. Continue?')) return;
    try {
      setSending(true);
      const res = await reportSettingsAPI.sendNow();
      if (res.data?.success) { toast.success('Report triggered! Emails will be sent shortly.'); fetchSettings(); }
      else toast.error(res.data?.message || 'Failed to trigger report');
    } catch (err) { console.error('Send now error:', err); toast.error(err.response?.data?.message || 'Failed to trigger report'); }
    finally { setSending(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (<div className="flex items-center justify-center py-12"><svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></div>);
  }

  if (error) {
    return (<Card className="text-center py-12"><HiOutlineExclamation className="w-12 h-12 text-danger-500 mx-auto mb-3" /><p className="text-secondary-700 mb-4">{error}</p><Button variant="outline" onClick={fetchSettings} icon={<HiOutlineRefresh className="w-4 h-4" />}>Retry</Button></Card>);
  }

  const recipients = (current.customRecipients || []).map((entry) => typeof entry === 'string' ? { email: entry, frequencies: ['daily'] } : entry);

  // Pagination for recipient cards
  const totalRecipients = recipients.length;
  const totalPages = Math.max(1, Math.ceil(totalRecipients / RECIPIENT_PAGE_SIZE));
  const safePage = Math.min(recipientPage, totalPages);
  const pagedRecipients = recipients.slice((safePage - 1) * RECIPIENT_PAGE_SIZE, safePage * RECIPIENT_PAGE_SIZE);

  // Generate initials from name or email
  const getInitials = (entry) => {
    const name = (typeof entry === 'object' && entry.name) ? entry.name.trim() : '';
    const email = typeof entry === 'string' ? entry : entry.email;
    if (name) {
      const parts = name.split(' ').filter(Boolean);
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase();
    }
    return email ? email[0].toUpperCase() : '?';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center"><HiOutlineDocumentReport className="w-5 h-5 text-primary-600" /></div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Daily Report</h2>
            <p className="text-sm text-gray-500">Configure automated PDF report emails</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSendNow} loading={sending} icon={<HiOutlineMail className="w-4 h-4" />}>Send Now</Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving} icon={<HiOutlineCheck className="w-4 h-4" />}>Save Settings</Button>
        </div>
      </div>

      {/* Enable Toggle */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-secondary-900">Enable Report</h3>
            <p className="text-sm text-secondary-500 mt-1">Automatically send a work report PDF to configured recipients.</p>
          </div>
          <Toggle checked={current.enabled} onChange={(val) => updateField('enabled', val)} label="" />
        </div>
      </Card>

      {/* Schedule Settings */}
      <Card className={current.enabled ? '' : 'opacity-50 pointer-events-none'}>
        <h3 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2"><HiOutlineClock className="w-5 h-5 text-primary-500" />Schedule</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Send Time</label>
            <TimePicker value={current.sendTime} onChange={(val) => updateField('sendTime', val)} alwaysOn hideLabel />
          </div>
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-1.5">Timezone</label>
            <select value={current.timezone} onChange={(e) => updateField('timezone', e.target.value)} className="w-full px-3.5 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200">
              {TIMEZONE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>
        {current.lastSentDate && (
          <div className="mt-4 p-3 bg-success-50 rounded-lg border border-success-200">
            <p className="text-sm text-success-700"><strong>Last sent:</strong> {new Date(current.lastSentDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        )}
      </Card>

      {/* Recipients */}
      <Card className={current.enabled ? '' : 'opacity-50 pointer-events-none'}>
        <h3 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2"><HiOutlineMail className="w-5 h-5 text-primary-500" />Report Recipients</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Toggle checked={current.recipientRoles?.admin !== false} onChange={(val) => updateRecipientRoles('admin', val)} label="Admins — Send organization-wide summary" />
            <button type="button" onClick={() => openRoleScheduleModal('admin')} className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-400 hover:text-primary-600 transition-colors" title="Configure admin schedule"><HiOutlineCog className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center justify-between">
            <Toggle checked={current.recipientRoles?.telecaller !== false} onChange={(val) => updateRecipientRoles('telecaller', val)} label="Telecallers — Send individual work reports" />
            <button type="button" onClick={() => openRoleScheduleModal('telecaller')} className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-400 hover:text-primary-600 transition-colors" title="Configure telecaller schedule"><HiOutlineCog className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Custom email recipients — card grid with pagination */}
        <div className="mt-5 pt-5 border-t border-secondary-200">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-secondary-800">Custom Email Recipients</h4>
            <Button variant="outline" size="sm" onClick={openAddModal} icon={<HiOutlinePlus className="w-4 h-4" />}>Add Recipient</Button>
          </div>
          <p className="text-xs text-secondary-500 mb-3">Each recipient can have its own schedule and frequency.</p>
          {totalRecipients > 0 && (
            <button type="button" onClick={() => setShowRecipients((prev) => !prev)} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors mb-3">
              <svg className={`w-4 h-4 transition-transform duration-200 ${showRecipients ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              {showRecipients ? `Hide recipients (${totalRecipients})` : `Show recipients (${totalRecipients})`}
            </button>
          )}

          {totalRecipients === 0 ? (
            <div className="py-8 text-center">
              <HiOutlineMail className="w-10 h-10 text-secondary-300 mx-auto mb-3" />
              <p className="text-sm text-secondary-400 mb-1">No custom recipients yet</p>
              <button type="button" onClick={openAddModal} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">Add your first recipient</button>
            </div>
          ) : showRecipients ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {pagedRecipients.map((entry, idx) => {
                  const globalIdx = (safePage - 1) * RECIPIENT_PAGE_SIZE + idx;
                  const email = typeof entry === 'string' ? entry : entry.email;
                  const name = (typeof entry === 'object' && entry.name) ? entry.name : '';
                  const displayName = name || email;
                  const initials = getInitials(entry);
                  return (
                    <div key={globalIdx} className="relative flex items-start gap-3 p-4 bg-white border border-secondary-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-all duration-200 group">
                      {/* Avatar */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold select-none">
                        {initials}
                      </div>
                      {/* Info — name primary, email secondary */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-sm font-semibold text-secondary-900 truncate">{displayName}</p>
                        {name && <p className="text-xs text-secondary-500 truncate">{email}</p>}
                      </div>
                      {/* Actions — top-right */}
                      <div className="absolute top-2.5 right-2.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => openEditModal(globalIdx)} className="p-1.5 rounded-lg hover:bg-primary-50 text-secondary-400 hover:text-primary-600 transition-colors" aria-label={`Edit ${email}`}><HiOutlinePencilAlt className="w-4 h-4" /></button>
                        <button type="button" onClick={() => removeCustomRecipient(globalIdx)} className="p-1.5 rounded-lg hover:bg-danger-50 text-secondary-400 hover:text-danger-500 transition-colors" aria-label={`Remove ${email}`}><HiOutlineTrash className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="mt-3">
                  <Pagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    totalItems={totalRecipients}
                    pageSize={RECIPIENT_PAGE_SIZE}
                    onPageChange={setRecipientPage}
                    showPageSizeSelector={false}
                    showSummary={true}
                  />
                </div>
              )}
              <p className="text-xs text-secondary-400 mt-2">{safePage * RECIPIENT_PAGE_SIZE - RECIPIENT_PAGE_SIZE + pagedRecipients.length}/{totalRecipients} recipients · Click the edit icon to configure schedule details</p>
            </>
          ) : null}
        </div>
      </Card>

      {/* Admin Report Fields */}
      <Card className={current.enabled ? '' : 'opacity-50 pointer-events-none'}>
        <CheckboxGroup title="Admin Report Fields" fields={ADMIN_FIELDS} values={current.adminFields || {}} onChange={updateAdminFields} />
      </Card>

      {/* Telecaller Report Fields */}
      <Card className={current.enabled ? '' : 'opacity-50 pointer-events-none'}>
        <CheckboxGroup title="Telecaller Report Fields" fields={TELECALLER_FIELDS} values={current.telecallerFields || {}} onChange={updateTelecallerFields} />
      </Card>

      {/* Info Footer */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
        <p className="text-sm text-primary-700"><strong>How it works:</strong> The report scheduler checks every minute if any organization's configured send time matches the current time. When it matches (and the frequency condition is met), it generates a PDF report with the selected fields and emails it to the configured recipients. Each custom recipient can have its own frequency and schedule day.</p>
      </div>

      {/* Recipient Modal */}
      <RecipientModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditIndex(null); }}
        onSave={handleModalSave}
        recipient={editIndex !== null ? recipients[editIndex] : null}
        orgDefaults={{ sendTime: current.sendTime, timezone: current.timezone }}
        isEdit={editIndex !== null}
      />

      {/* Role Schedule Modal */}
      <RoleScheduleModal
        isOpen={roleScheduleModal.open}
        onClose={() => setRoleScheduleModal({ open: false, role: null })}
        onSave={handleRoleScheduleSave}
        role={roleScheduleModal.role}
        initialData={current.recipientRoleSchedules?.[roleScheduleModal.role] || {}}
        orgDefaults={{ sendTime: current.sendTime, timezone: current.timezone, frequency: current.frequency, weeklyDay: current.weeklyDay, monthlyDay: current.monthlyDay }}
      />
    </div>
  );
};

export default ReportSettings;